import React, { useEffect, useMemo, useState } from "react";
import type { Auth } from "firebase/auth";
import type { Firestore } from "firebase/firestore";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";
import { toYMD } from "../lib/inventoryHelpers";
import type { ReportFilters } from "../lib/schema";
import type { PreparedImpactDisplay } from "../lib/impact";
import { getImpactPreviewForFilters } from "../services/reportPreview";

type CustomerOpt = { key: string; name: string };
type TypeOpt = { id: string; label: string };

type Params = {
  db: Firestore;
  auth: Auth;
  user: any;
  authReady: boolean;
  isCustomerPortal: boolean;
  customerKeys: string[] | null | undefined;
  loadProductTypesForImpact: () => Promise<any>;
  customerOptsState?: [CustomerOpt[], React.Dispatch<React.SetStateAction<CustomerOpt[]>>];
};

export function useReportFilters({
  db,
  auth,
  user,
  authReady,
  isCustomerPortal,
  customerKeys,
  loadProductTypesForImpact,
  customerOptsState,
}: Params) {
  const [reportPreview, setReportPreview] = useState<PreparedImpactDisplay | null>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);

  const [rpFrom, setRpFrom] = useState<string>(toYMD(new Date(Date.now() - 30 * 86400000)));
  const [rpTo, setRpTo] = useState<string>(toYMD(new Date()));
  const [rpCustomers, setRpCustomers] = useState<string>("");
  const [rpTypes, setRpTypes] = useState<string>("");
  // Hook måste anropas ovillkorligt: skapa intern fallback och välj sedan
  const customerOptsStateInternal = useState<CustomerOpt[]>([]);
  const [rpCustomerOpts, setRpCustomerOpts] = customerOptsState ?? customerOptsStateInternal;
  const [rpSelectedCustomers, setRpSelectedCustomers] = useState<string[]>([]);
  const [rpSelectedTypes, setRpSelectedTypes] = useState<string[]>([]);
  const [rpTypeOpts, setRpTypeOpts] = useState<TypeOpt[]>([]);
  const [showNewProductTypeInput, setShowNewProductTypeInput] = useState<boolean>(false);
  const [newProductType, setNewProductType] = useState<string>("");
  const [rpTypesPrimed, setRpTypesPrimed] = useState(false);

  const productTypeExists = useMemo(() => {
    const v = newProductType.trim();
    if (!v) return false;
    return rpTypeOpts.some((t) => t.label.trim().toLowerCase() === v.toLowerCase());
  }, [newProductType, rpTypeOpts]);

  useEffect(() => {
    if (!authReady || !auth.currentUser) return;
    if (rpTypesPrimed) return;
    let cancelled = false;
    (async () => {
      try {
        await loadProductTypesForImpact();
        if (!cancelled) setRpTypesPrimed(true);
      } catch (e) {
        console.warn("Kunde inte prima productTypes för impact:", e);
      }
    })();
    return () => { cancelled = true; };
  }, [authReady, user?.uid, rpTypesPrimed, loadProductTypesForImpact, auth]);

  useEffect(() => {
    if (rpSelectedTypes.length === 0 && rpTypeOpts.length > 0) {
      setRpSelectedTypes(rpTypeOpts.map((o) => o.id));
    }
  }, [rpTypeOpts]);

  useEffect(() => {
    if (!authReady || !auth.currentUser) return;
    let cancelled = false;
    (async () => {
      try {
        let opts: CustomerOpt[];
        if (isCustomerPortal) {
          // Firestore-regler är inte filter. Ett kundkonto får därför inte
          // fråga efter hela kundregistret och filtrera i webbläsaren, utan
          // hämtar endast de dokument som kontot uttryckligen tilldelats.
          const assignedKeys = Array.from(
            new Set((customerKeys ?? []).map(String).map((key) => key.trim()).filter(Boolean))
          );
          const assignedDocs = await Promise.all(
            assignedKeys.map(async (key) => ({ key, snap: await getDoc(doc(db, "customers", key)) }))
          );
          opts = assignedDocs
            .filter(({ snap }) => snap.exists())
            .map(({ key, snap }) => {
              const data = snap.data() as any;
              return { key, name: String(data?.name ?? key) };
            });
        } else {
          const snap = await getDocs(collection(db, "customers"));
          opts = snap.docs.map((d) => {
            const data = d.data() as any;
            return { key: d.id, name: String(data?.name ?? d.id) };
          });
        }
        opts.sort((a, b) => a.name.localeCompare(b.name, "sv"));

        if (!cancelled) {
          setRpCustomerOpts(opts);
          if (rpSelectedCustomers.length === 0 && opts.length > 0) {
            setRpSelectedCustomers(opts.map((o) => o.key));
          }
        }
      } catch (e) {
        console.warn("Kunde inte ladda customers:", e);
        if (!cancelled) setRpCustomerOpts([]);
      }
    })();

    return () => { cancelled = true; };
  }, [authReady, user?.uid, isCustomerPortal, JSON.stringify(customerKeys)]);

  useEffect(() => {
    if (!authReady || !auth.currentUser) return;
    let unsub: undefined | (() => void);
    try {
      const colRef = collection(db, "productTypes");
      unsub = onSnapshot(
        query(colRef, orderBy("label")),
        (snap) => {
          const opts = snap.docs
            .map((d) => {
              const data = d.data() as any;
              const id = String(d.id);
              const label = typeof data?.label === "string" ? data.label : id;
              const active = data?.active;
              return { id, label, active };
            })
            .filter((o) => o.active !== false)
            .map(({ id, label }) => ({ id, label }))
            .sort((a, b) => a.label.localeCompare(b.label, "sv"));

          setRpTypeOpts(opts);
          setRpSelectedTypes((prev) => (prev && prev.length > 0 ? prev : opts.map((o) => o.id)));
        }
      );
    } catch (e) {
      console.warn("Kunde inte live-ladda productTypes:", e);
      setRpTypeOpts([]);
    }

    return () => { if (unsub) unsub(); };
  }, [authReady, user?.uid]);

  React.useEffect(() => {
    if (rpSelectedTypes.length === 0 && rpTypeOpts.length > 0) {
      setRpSelectedTypes(rpTypeOpts.map((o) => o.id));
    }
  }, [rpTypeOpts]);

  async function loadReportPreview(filters: ReportFilters) {
    setReportLoading(true);
    setReportError(null);
    try {
      const { preview } = await getImpactPreviewForFilters(filters);
      setReportPreview(preview);
    } catch (e: any) {
      setReportPreview(null);
      setReportError(e?.message || "Kunde inte hämta rapportdata.");
    } finally {
      setReportLoading(false);
    }
  }

  async function handleLoadPreviewClick() {
    const customerIds = rpCustomers.split(",").map(s => s.trim()).filter(Boolean);
    const productTypes = rpTypes.split(",").map(s => s.trim()).filter(Boolean);

    const filters: ReportFilters = {
      fromDate: toYMD(new Date()),
      toDate: toYMD(new Date()),
      basis: "completedAt",
      customerIds,
      productTypes: productTypes.length ? (productTypes as any) : undefined,
    };

    await loadReportPreview(filters);
  }

  return {
    reportPreview,
    reportLoading,
    reportError,
    rpFrom,
    setRpFrom,
    rpTo,
    setRpTo,
    rpCustomers,
    setRpCustomers,
    rpTypes,
    setRpTypes,
    rpCustomerOpts,
    setRpCustomerOpts,
    rpSelectedCustomers,
    setRpSelectedCustomers,
    rpSelectedTypes,
    setRpSelectedTypes,
    rpTypeOpts,
    setRpTypeOpts,
    showNewProductTypeInput,
    setShowNewProductTypeInput,
    newProductType,
    setNewProductType,
    productTypeExists,
    rpTypesPrimed,
    setRpTypesPrimed,
    loadReportPreview,
    handleLoadPreviewClick,
  };
}
